/* @flow */
import compose from 'ramda/src/compose';
import curry from 'ramda/src/curry';

import { tryCatch, aliasMethod } from './Either.js';

const identity = x => x;

/**
 * Wraps the execution of a function so that:
 *
 * - Execution is delayed until the calling code runs `either`
 * - Errors can be handled in a functional way with either
 * - We can easily build up a lazy computation based on the IO using map
 *
 * The last point is the biggest win of IO: It's lazy, meaning we can map away
 * all we want and it just builds up a computation. It will only be called at
 * the end when it is `fold`ed.
 *
 * NOTE: This is not strictly for IO, so IO may be a misnomer. It is really
 * useful for any code that has side effects or might throw an error. The latter
 * case is useful for building up a large lazy computation that could throw at
 * any number of points along the way. Since execution is delayed until fold is
 * called and it is tryCatch wrapped it doesn't matter where in the execution it
 * fails.
 */

export const IO = (f: Function) => ({
  fn: f,
  map: (fs: Function) => IO(compose(fs, f)),
  chain: (g: Function) => IO(() => {
    const next = g(f()); // Must return IO
    return next.fn();
  }),

  fold: (left: Function, right: Function) =>
    tryCatch(f).fold(left, right),

  // A shortcut for when we really just want to handle the left case and want to
  // use the identity function for the right case
  foldOr: (left: Function) =>
    tryCatch(f).fold(left, identity),

  either: aliasMethod('fold'),
});

IO.of = (x: any) => IO(() => x);

// A curried foldOr for use in fn pipelines
IO.foldOr = curry((f: Function, io: IO) => io.foldOr(f));




// WEBPACK FOOTER //
// ./web_modules/trustar/fp/IO.js